import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

export const useUnsavedChangesModal = ({
  hasChanges,
  isLockedPeriod,
  pendingReportTypeId,
  clearPendingReportTypeId,
  applyReportTypeChange,
  pendingFundId,
  clearPendingFundId,
  applyFundChange,
  revertCurrentFundChanges,
  onSave
}) => {
  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }) =>
      !isLockedPeriod && hasChanges && currentLocation.pathname !== nextLocation.pathname,
    [isLockedPeriod, hasChanges]
  );

  const blocker = useBlocker(shouldBlockNavigation);
  const isUnsavedModalOpen =
    blocker.state === 'blocked' || pendingReportTypeId != null || pendingFundId != null;

  useEffect(() => {
    if (!hasChanges || isLockedPeriod) return;
    const handleBeforeUnload = e => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, isLockedPeriod]);

  const handleUnsavedModalBack = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset();
    clearPendingReportTypeId();
    clearPendingFundId();
  }, [blocker, clearPendingReportTypeId, clearPendingFundId]);

  const handleUnsavedModalDiscard = useCallback(() => {
    if (pendingFundId != null && revertCurrentFundChanges) {
      revertCurrentFundChanges();
    }
    if (blocker.state === 'blocked') blocker.proceed();
    if (pendingReportTypeId != null) applyReportTypeChange(pendingReportTypeId);
    if (pendingFundId != null) applyFundChange(pendingFundId);
  }, [
    blocker,
    pendingReportTypeId,
    applyReportTypeChange,
    pendingFundId,
    applyFundChange,
    revertCurrentFundChanges
  ]);

  const handleUnsavedModalSave = useCallback(() => {
    onSave({
      callbackFn: () => {
        if (pendingReportTypeId != null) {
          applyReportTypeChange(pendingReportTypeId);
        }
        if (pendingFundId != null) {
          applyFundChange(pendingFundId);
        }
        if (blocker.state === 'blocked') {
          blocker.proceed();
        }
      }
    });
  }, [onSave, pendingReportTypeId, applyReportTypeChange, pendingFundId, applyFundChange, blocker]);

  return {
    isUnsavedModalOpen,
    handleUnsavedModalBack,
    handleUnsavedModalDiscard,
    handleUnsavedModalSave
  };
};
